import { Button, Grid, styled, TextField, Paper, Box, Typography, IconButton } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarEntryEntity, CustomCalendarEntryTypesEntity } from '../../../api/generated';
import { calendarEntriesApi, employeesApi } from '../../../api';
import AppModal from '../../../components/AppModal';
import { checkTokenRole } from 'utils/checkToken';
import CalendarInput from 'components/input-fields/CalendarInput';
import * as Yup from 'yup';
import { isEndDateValid, isValidDate } from 'utils/dateValidator';
import { useFormik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import { enUS, de, fr, it } from 'date-fns/locale';
import toast from 'react-hot-toast';

export interface UpdateEvent {
  startedOn: string,
  endedOn: string,
  publicNotes?: string | null,
  managerNotes?: string | null
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  id: number;
  employeeId: number;
  seasonId: number;
  color: string;
  fetchCalendarData: () => void;
  userMode: boolean;
  updateEvent: (calendarEntry: UpdateEvent, id: number) => void;
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 700,
  minWidth: 400,
  [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)',
  '& .close-button': {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
    },
  }
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.default,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.action.hover,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.action.selected,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  textTransform: 'none',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  transition: 'all 0.2s ease-in-out',
  '&.MuiButton-contained': {
    boxShadow: 'none',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
  },
  '&.MuiButton-outlined': {
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
  borderRadius: '16px',
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    opacity: 0.8,
    fontSize: '1.2rem',
  },
}));

const NotesSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiTextField-root': {
    marginBottom: theme.spacing(1.5),
  },
}));

const DateSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '16px',
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  gap: theme.spacing(1.5),
  justifyContent: 'flex-end',
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

type EventData = Omit<CalendarEntryEntity, 'createdById' | 'updatedById'> & {
  createdBy?: string;
  updatedBy?: string;
  customType?: CustomCalendarEntryTypesEntity;
};

const ViewEventModal: FC<ModalProps> = ({ open, onClose, id, color, fetchCalendarData, employeeId, seasonId, userMode, updateEvent }) => {
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState<EventData>();
  const isChief = checkTokenRole() === 'DEPCHIEF';
  const userRole = checkTokenRole();
  const canEditManagerNotes = ['MANAGER', 'CONTROLLER', 'OWNER'].includes(userRole);

  // Get the appropriate date-fns locale based on current language
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'de':
        return de;
      case 'fr':
        return fr;
      case 'it':
        return it;
      default:
        return enUS;
    }
  };

  const initialValues = {
    startedOn: event?.startedOn ? new Date(event.startedOn) : new Date(),
    endedOn: event?.endedOn ? new Date(event.endedOn) : new Date(),
    publicNotes: event?.publicNotes || '',
    managerNotes: event?.managerNotes || '',
  };

  const validationSchema = Yup.object().shape({
    startedOn: Yup.date().required(
      t('common.forms.field.required', {
        field: t('employees.calendar.entry.startedOn'),
      }),
    ).typeError(t('employees.calendar.typeError'))
      .test('is-valid-date', t('employees.calendar.typeError'), (value) => {
        return isValidDate(value);
      }),
    endedOn: Yup.date()
      .typeError(t('employees.calendar.typeError'))
      .required(t('common.forms.field.required', {
        field: t('employees.calendar.entry.endedOn'),
      }),)
      .test('isValidDate', t('employees.calendar.typeError'), (value) => {
        return isValidDate(value);
      })
      .test('isEndDateValid', t('employees.calendar.entry.endDateAfterStartDate'), function (value) {
        return isEndDateValid(value, this.parent.startedOn);
      }),
    publicNotes: Yup.string().notRequired(),
    managerNotes: Yup.string().notRequired(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (data) => {
      const updatedData = {
        startedOn: data.startedOn.toISOString(),
        endedOn: data.endedOn.toISOString(),
        publicNotes: data.publicNotes,
        managerNotes: data.managerNotes,
      }
      updateEvent(updatedData, id);
      onClose();
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  function formatDate(date: Date): string {
    return format(date, 'EEEE, MMMM d, yyyy', { locale: getDateLocale() });
  }

  function formatDateTime(dateString: string): string {
    return format(new Date(dateString), 'EEEE, MMMM d, yyyy • HH:mm', { locale: getDateLocale() });
  }

  // Function to get the display name for the event type (handles causali + custom events)
  function getEventDisplayName(event: EventData): string {
    const absenceType = (event as any).absenceType;
    if (absenceType) {
      const lang = (i18n.language || 'en').slice(0, 2);
      return lang === 'it' ? absenceType.labelIt
        : lang === 'de' ? absenceType.labelDe
        : lang === 'fr' ? absenceType.labelFr
        : absenceType.labelEn;
    }
    if (event.type === 'CUSTOM' && event.customType) {
      return event.customType.name;
    }
    return t(`employees.calendar.types.${event.type.toLowerCase()}`);
  }

  // Function to format time (HH:mm format)
  function formatTime(timeString: string): string {
    if (!timeString) return '';
    // Ensure the time is in HH:mm format
    return timeString;
  }

  async function handleDelete() {
    if (!userMode) {
      try {
        await calendarEntriesApi.remove(String(id), seasonId, employeeId);
        fetchCalendarData();
        onClose();
      } catch (error: any) {
        if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
          toast.error(t('employees.calendar.entry.cannotModifyPast'));
        } else {
          toast.error(t('employees.calendar.entry.errorDeleting'));
        }
      }
    }
  }

  async function handleAccept() {
    if (!userMode) {
      try {
        await calendarEntriesApi.accept(String(id), seasonId, employeeId);
        fetchCalendarData();
        onClose();
      } catch (error: any) {
        if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
          toast.error(t('employees.calendar.entry.cannotModifyPast'));
        } else {
          toast.error(error?.response?.data?.message || t('employees.calendar.entry.errorDeleting'));
        }
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const { data } = userMode ? await calendarEntriesApi.findOneByMyself(String(id), String(employeeId))
        : await calendarEntriesApi.findOne(String(id), String(employeeId), seasonId);
      const { createdById, updatedById, ...rest } = data;

      async function fetchName(employeeId: number) {
        const { data: employee } = userMode ? await employeesApi.findMyselfWithoutSeaoson(
          String(employeeId),
        ) : await employeesApi.findOneWithoutSeaoson(
          String(employeeId),
        );
        return `${employee.firstName} ${employee.lastName}`;
      }

      if (!isChief) {
        const [createdBy, updatedBy] = await Promise.all([
          createdById ? fetchName(createdById) : undefined,
          updatedById ? fetchName(updatedById) : undefined,
        ]);
        setEvent({
          ...rest,
          createdBy,
          updatedBy,
        });
      }
      else {
        setEvent(rest);
      }
    }

    if (open) {
      fetchData();
    }
  }, [open]);

  return event ? (
    <StyledAppModal open={open} handleClose={onClose} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <HeaderSection sx={{ backgroundColor: color }}>
        <IconButton className="close-button" onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {/* Show color indicator for custom events */}
          {event.type === 'CUSTOM' && event.customType && (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: event.customType.colour || '#FF0000',
                border: '2px solid rgba(255,255,255,0.3)',
                flexShrink: 0
              }}
            />
          )}
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
            {getEventDisplayName(event)}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
          {event.accepted ? t('employees.calendar.entry.accepted') : t('employees.calendar.entry.pending')}
          {event.startTime && event.endTime && (
            <span style={{ marginLeft: '8px' }}>
              • {t('employees.calendar.entry.timed')}
            </span>
          )}
        </Typography>
      </HeaderSection>

      <ContentSection>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DateSection>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('employees.calendar.entry.startedOn')}
                    </Typography>
                    <CalendarInput
                      disabled={userMode}
                      value={values.startedOn}
                      onChange={(v) => {
                        if (v && !isNaN(+v)) {
                          handleChange({ target: { name: 'startedOn', value: v } });
                        }
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.startedOn,
                          helperText: errors.startedOn ? String(errors.startedOn) : undefined,
                          fullWidth: true,
                          placeholder: formatDate(new Date()),
                          size: "small",
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('employees.calendar.entry.endedOn')}
                    </Typography>
                    <CalendarInput
                      disabled={userMode}
                      value={values.endedOn}
                      onChange={(v) => {
                        if (v && !isNaN(+v)) {
                          handleChange({ target: { name: 'endedOn', value: v } });
                        }
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.endedOn,
                          helperText: errors.endedOn ? String(errors.endedOn) : undefined,
                          fullWidth: true,
                          placeholder: formatDate(new Date()),
                          size: "small",
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Show start and end times if they exist */}
                  {(event.startTime || event.endTime) && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                          {t('shift.startTime')} & {t('shift.endTime')}
                        </Typography>
                        <Grid container spacing={2}>
                          {event.startTime && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatTime(event.startTime)}
                              </Typography>
                            </Grid>
                          )}
                          {event.endTime && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatTime(event.endTime)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DateSection>
            </Grid>

            {/* Event Type Information */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {event.type === 'CUSTOM' && event.customType && (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: event.customType.colour || '#FF0000',
                        border: '1px solid #ddd',
                        flexShrink: 0
                      }}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('employees.calendar.entry.type.label')}:</strong> {getEventDisplayName(event)}
                    {event.startTime && event.endTime && (
                      <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                        ({t('employees.calendar.entry.timedEvent')})
                      </span>
                    )}
                  </Typography>
                </Box>
                {(event as any).certificateStatus && (event as any).certificateStatus !== 'NONE' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>{t('absence.certificate.status')}:</strong>{' '}
                    {t(`absence.certificate.status${((event as any).certificateStatus.charAt(0) + (event as any).certificateStatus.slice(1).toLowerCase())}`)}
                    {(event as any).protocolNumber && (
                      <span style={{ marginLeft: '8px' }}>
                        · {t('absence.certificate.protocolShort')} {(event as any).protocolNumber}
                      </span>
                    )}
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <NotesSection>
                <StyledTextField
                  fullWidth
                  multiline
                  rows={2}
                  name="publicNotes"
                  label={t('employees.calendar.entry.publicNotes')}
                  value={values.publicNotes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={userMode}
                  error={Boolean(touched.publicNotes && errors.publicNotes)}
                  helperText={touched.publicNotes && errors.publicNotes}
                  placeholder={t('employees.calendar.notesPlaceholder')}
                  size="small"
                />

                {canEditManagerNotes && (
                  <StyledTextField
                    fullWidth
                    multiline
                    rows={2}
                    name="managerNotes"
                    label={t('employees.calendar.entry.managerNotes')}
                    value={values.managerNotes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={userMode}
                    error={Boolean(touched.managerNotes && errors.managerNotes)}
                    helperText={touched.managerNotes && errors.managerNotes}
                    placeholder={t('employees.calendar.notesPlaceholder')}
                    size="small"
                  />
                )}
              </NotesSection>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: 'background.default' }}>
                {!isChief && (
                  <InfoItem>
                    <PersonIcon />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('employees.calendar.entry.createdBy')}
                      </Typography>
                      <Typography variant="body2">
                        {event.createdBy ?? t('common.deletedUser')}
                      </Typography>
                    </Box>
                  </InfoItem>
                )}

                <InfoItem>
                  <AccessTimeIcon />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('employees.calendar.entry.createdAt')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(event.createdAt)}
                    </Typography>
                  </Box>
                </InfoItem>

                {!isChief && event.updatedBy && (
                  <InfoItem>
                    <PersonIcon />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('employees.calendar.entry.acceptedBy')}
                      </Typography>
                      <Typography variant="body2">
                        {event.updatedBy}
                      </Typography>
                    </Box>
                  </InfoItem>
                )}

                {event.updatedAt && (
                  <InfoItem>
                    <AccessTimeIcon />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('employees.calendar.entry.acceptedAt')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(event.updatedAt)}
                      </Typography>
                    </Box>
                  </InfoItem>
                )}
              </Paper>
            </Grid>
          </Grid>

          <ActionButtonsContainer>
            {!userMode && event.accepted && (
              <>
                <ActionButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  size="small"
                >
                  {t('employees.calendar.entry.deleteEntry')}
                </ActionButton>
                <ActionButton
                  variant="contained"
                  type="submit"
                  startIcon={<EditIcon />}
                  size="small"
                >
                  {t('save')}
                </ActionButton>
              </>
            )}

            {!userMode && !event.accepted && (
              <>
                <ActionButton
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleAccept}
                  size="small"
                >
                  {t('employees.calendar.entry.acceptEntry')}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  size="small"
                >
                  {t('employees.calendar.entry.rejectEntry')}
                </ActionButton>
              </>
            )}
          </ActionButtonsContainer>
        </form>
      </ContentSection>
    </StyledAppModal>
  ) : (
    <></>
  );
};

export default ViewEventModal;
